import { useEffect, useRef, useState, type CSSProperties } from 'react'

/**
 * SupportButton — the brand-standard "Support" escape hatch, shared across
 * every Parkify customer surface (consumer checkout, account portal, …) so
 * support looks and behaves identically everywhere.
 *
 * Tapping it opens a small menu offering the two live Avery channels: Text
 * (SMS) and Call (voice). Both route to Avery's single Twilio line — Avery
 * handles inbound SMS (parkify-cs conversation engine) and inbound voice
 * (Avery Voice intake), and identifies the customer by their caller/sender
 * number, so this works on anonymous and authenticated surfaces alike.
 *
 * The popover is identical in every tone; only the trigger pill restyles:
 *   - `light` (default): soft-purple pill for light headers (checkout).
 *   - `dark`: white-on-translucent pill for the portal's purple-gradient header.
 *
 * Styling is inline (not CSS classes) so the component is fully portable — it
 * needs only the global @parkify/brand-tokens custom properties the host app
 * already loads, and brings no stylesheet of its own.
 *
 * Voice/text are both surfaced now; channels can be dialed back later by
 * trimming CHANNELS.
 */

export type SupportTone = 'light' | 'dark'

// Avery's live support line (Twilio): handles both SMS and voice.
// E.164 for tel:/sms: links; the pretty form is display-only.
const AVERY_E164 = '+18559175072'
const AVERY_PRETTY = '1 (855) 917-5072'
// Prefilled SMS body so the first text gives Avery something to work with.
const SMS_BODY = 'Hi Avery — I need help with parking.'

const HeadsetIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 14v-2a9 9 0 0 1 18 0v2" />
    <path d="M21 16a2 2 0 0 1-2 2h-1a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1h3zM3 16a2 2 0 0 0 2 2h1a1 1 0 0 0 1-1v-3a1 1 0 0 0-1-1H3z" />
    <path d="M21 14v3a4 4 0 0 1-4 4h-5" />
  </svg>
)

const ChatIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
)

const PhoneIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
)

const CHANNELS = [
  {
    key: 'text',
    href: `sms:${AVERY_E164}?&body=${encodeURIComponent(SMS_BODY)}`,
    Icon: ChatIcon,
    label: 'Text Avery',
    sub: 'Quick answers by SMS',
  },
  {
    key: 'call',
    href: `tel:${AVERY_E164}`,
    Icon: PhoneIcon,
    label: 'Call Avery',
    sub: AVERY_PRETTY,
  },
] as const

// Trigger-pill treatment per tone. The popover is tone-independent.
const triggerStyle: Record<SupportTone, { base: CSSProperties; hover: CSSProperties }> = {
  light: {
    base: {
      color: 'var(--color-brand)',
      background: 'var(--color-brand-light)',
      border: '1px solid var(--color-brand-border)',
    },
    hover: {
      background: 'var(--color-white)',
      borderColor: 'var(--color-brand)',
    },
  },
  dark: {
    base: {
      color: 'var(--color-white)',
      background: 'transparent',
      border: '1px solid rgba(255,255,255,0.3)',
    },
    hover: {
      background: 'rgba(255,255,255,0.10)',
      borderColor: 'rgba(255,255,255,0.45)',
    },
  },
}

const menuItemStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '10px',
  borderRadius: 'var(--radius-md)',
  textDecoration: 'none',
  color: 'var(--color-black)',
}

const iconWrapStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 34,
  height: 34,
  flex: '0 0 auto',
  borderRadius: 'var(--radius-full)',
  background: 'var(--color-brand-light)',
  color: 'var(--color-brand)',
}

export function SupportButton({ tone = 'light' }: { tone?: SupportTone }) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const t = triggerStyle[tone]

  // Close on outside-click and Escape — standard popover behavior.
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div style={{ position: 'relative', flex: '0 0 auto' }} ref={rootRef}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 7,
          fontSize: 13,
          fontWeight: 700,
          padding: '7px 14px',
          borderRadius: 'var(--radius-full)',
          cursor: 'pointer',
          whiteSpace: 'nowrap',
          transition: 'background 120ms ease, border-color 120ms ease',
          ...t.base,
          ...(open ? t.hover : null),
        }}
        onMouseEnter={(e) => Object.assign(e.currentTarget.style, t.hover)}
        onMouseLeave={(e) => Object.assign(e.currentTarget.style, open ? t.hover : t.base)}
      >
        <HeadsetIcon />
        Support
      </button>

      {open && (
        <div
          role="menu"
          aria-label="Contact Avery"
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            right: 0,
            zIndex: 30,
            width: 244,
            background: 'var(--color-white)',
            border: '1px solid var(--color-brand-border)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-lg)',
            padding: 6,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {CHANNELS.map(({ key, href, Icon, label, sub }) => (
            <a
              key={key}
              role="menuitem"
              href={href}
              onClick={() => setOpen(false)}
              style={menuItemStyle}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-brand-light)' }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent' }}
            >
              <span style={iconWrapStyle}><Icon /></span>
              <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
                <span style={{ fontSize: 14, fontWeight: 700 }}>{label}</span>
                <span style={{ fontSize: 12, color: 'var(--color-gray-500)' }}>{sub}</span>
              </span>
            </a>
          ))}
        </div>
      )}
    </div>
  )
}
